
import React, { useState, useEffect, useRef, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/auth";
import { Navigate } from "react-router-dom";
import { fetchMessageThreads, MessageThread } from "@/services/messages";
import MessageList from "@/components/messages/MessageList";
import { Inbox } from "lucide-react";
import { toast } from "sonner";
import { createLogger } from "@/services/utils/loggingUtils";

const logger = createLogger('MessagesPage');
const FETCH_COOLDOWN_MS = 30000; // 30 seconds

const MessagesPage: React.FC = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState<MessageThread[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const mountedRef = useRef(false);
  const refreshTimerRef = useRef<number | null>(null);
  const lastFetchTimeRef = useRef<number>(0);

  // Redirect if not logged in
  if (!user) {
    return <Navigate to="/auth/login" />;
  }

  // Load message threads once on mount
  useEffect(() => {
    // Prevent duplicate fetches
    if (mountedRef.current) return;
    mountedRef.current = true;
    
    loadThreads();

    // Clean up function
    return () => {
      mountedRef.current = false;
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []);

  // Load threads function - memoized to avoid recreation
  const loadThreads = useCallback(async (force: boolean = false) => {
    const now = Date.now();
    
    // Apply rate limiting to prevent excessive API calls
    if (!force && now - lastFetchTimeRef.current < FETCH_COOLDOWN_MS) {
      logger.debug("Skipping message thread fetch due to rate limiting");
      return;
    }
    
    setLoadingThreads(true);
    setError(null);
    lastFetchTimeRef.current = now;
    
    try {
      const data = await fetchMessageThreads();
      setThreads(data);
    } catch (error) {
      logger.error("Error fetching threads:", error);
      setError("Failed to load message threads");
      toast.error("Failed to load conversations");
    } finally {
      setLoadingThreads(false);
    }
  }, []);

  // Handle manual refresh from UI
  const handleManualRefresh = useCallback(() => {
    loadThreads(true); // Force refresh
    toast.success("Refreshing conversations...");
  }, [loadThreads]);

  // Set up visibility change listener for optimal refreshing
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refresh data when tab becomes visible if it's been at least 2 minutes
        const now = Date.now();
        if (now - lastFetchTimeRef.current > 120000) {
          loadThreads();
        }
      }
    };
    
    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadThreads]);

  // Refresh threads periodically with adaptive interval based on page visibility
  useEffect(() => {
    // Clear any existing timer first
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }
    
    // Use a longer refresh interval when tab is in background
    const refreshInterval = document.visibilityState === 'visible' ? 
      5 * 60 * 1000 : // 5 minutes when visible
      15 * 60 * 1000; // 15 minutes when in background
    
    // Set a new timer with adaptive interval
    refreshTimerRef.current = window.setInterval(async () => {
      // Skip refresh if component is unmounted
      if (!mountedRef.current) return;
      
      // Skip if page is not visible
      if (document.visibilityState !== 'visible') return;
      
      loadThreads();
    }, refreshInterval);
    
    // Update interval when visibility changes
    const handleVisibilityChange = () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
      
      const newInterval = document.visibilityState === 'visible' ? 
        5 * 60 * 1000 : // 5 minutes when visible
        15 * 60 * 1000; // 15 minutes when in background
      
      refreshTimerRef.current = window.setInterval(() => {
        if (mountedRef.current && document.visibilityState === 'visible') {
          loadThreads();
        }
      }, newInterval);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [loadThreads]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow py-8">
        <div className="container mx-auto px-4">
          <div className="mb-6">
            <h1 className="text-2xl font-bold flex items-center">
              <Inbox className="mr-2 h-6 w-6" /> Messages
            </h1>
          </div>
          
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="flex flex-col">
              <div className="p-4 border-b">
                <h2 className="font-semibold">Conversations</h2>
                {error && (
                  <p className="text-sm text-red-500 mt-1">{error}</p>
                )}
              </div>
              <div className="max-h-[70vh] overflow-y-auto">
                <MessageList 
                  threads={threads}
                  isLoading={loadingThreads}
                  onRefresh={handleManualRefresh}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default MessagesPage;
